/**
 * Рамка повторного входу поверх сторінки (UX-30; E3a-06b): перший екран має лише її, а форма
 * входу — лінивий чанк (`ReloginDialog.tsx`, бюджет першого екрана). Стан — `platform/relogin.ts`.
 *
 * Модальний `<dialog>` поза `#root` (портал у `body`): `#root` під шаром прихований
 * (`visibility: hidden`, `inert`), а діалог, відкритий пізніше за інші модальні (step-up,
 * «Незбережені зміни»), лежить над ними і єдиний приймає фокус. `Esc` шару не закриває: вийти можна
 * лише входом або «Вийти» (умова Security). Поки чанк вантажиться, видно порожнє тло шару — вміст
 * сторінки вже прихований.
 */
import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { shell as t } from '../../i18n/uk/shell.ts';
import { reloginUnavailable, useReloginOpen, watchRelogin } from '../../platform/relogin.ts';

const ReloginDialog = lazy(() =>
  import('./ReloginDialog.tsx').catch(() => {
    // Чанк старої збірки зник після релізу: вхід окремою сторінкою (введене тоді не врятувати).
    reloginUnavailable();
    return new Promise<never>(() => undefined);
  }),
);

export function ReloginHost() {
  const queryClient = useQueryClient();
  // `401 session_expired` від API і сигнали інших вкладок → шар (platform/relogin.ts).
  useEffect(() => watchRelogin(queryClient), [queryClient]);
  const open = useReloginOpen();
  if (!open || typeof document === 'undefined') return null;
  return createPortal(<ReloginFrame />, document.body);
}

function ReloginFrame() {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    dialog.focus();
    // Браузер може закрити модальний діалог сам (повторний Esc без дії людини в Chromium):
    // поки шар потрібен, відкриваємо його знову.
    const reopen = () => {
      if (dialog.isConnected && !dialog.open) dialog.showModal();
    };
    dialog.addEventListener('close', reopen);
    return () => dialog.removeEventListener('close', reopen);
  }, []);
  return (
    <dialog
      ref={ref}
      className="relogin"
      aria-modal="true"
      aria-label={t.reloginTitle}
      tabIndex={-1}
      data-testid="relogin"
      onCancel={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') e.preventDefault();
      }}
    >
      <Suspense fallback={null}>
        <ReloginDialog />
      </Suspense>
    </dialog>
  );
}
